import React from 'react';
import { Box, Typography } from '@mui/material';
import IPPGChart from './IPPGChart';
import ECGChart from './ECGChart';
import ICGChart from './ICGChart';
import BPWaveChart from './BPWaveChart';

interface SignalsPanelProps {
  isMonitoring: boolean;
  ppgSignal: number[];
  ecgSignal: number[];
  icgSignal: number[];
  bpSignal: number[];
  // Exact sample counter from the latest response (result.buffer_frames) —
  // shared by all four charts since they all come from the same response.
  bufferFrames?: number;
  // Which rows the current model can actually produce — e.g. the legacy
  // SeeVita v1/v2/v3 models never output ICG or a real BP waveform, and
  // their "ECG" is synthetic (derived from HR, not measured), so it's
  // treated as unavailable rather than shown as a real signal. Defaults to
  // all four shown, for callers that haven't wired model capabilities yet.
  showEcg?: boolean;
  showIcg?: boolean;
  showBp?: boolean;
  // Verification-demo overlay only (see RecordedDemoPage.tsx) — real
  // lab-device reference traces, pre-aligned to the same timeline as the
  // model's own signals. Omitted entirely on every live-monitoring page.
  groundTruth?: { ppg?: number[]; ecg?: number[]; icg?: number[]; bp?: number[] };
}

const tileSx = {
  position: 'relative' as const,
  borderRadius: '12px',
  overflow: 'hidden',
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid',
  borderColor: 'rgba(255,255,255,0.055)',
  minHeight: { xs: 80, md: 130, lg: 160 },
};

const labelSx = {
  fontSize: '0.58rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '2px',
  textTransform: 'uppercase' as const, position: 'absolute' as const, top: 8, left: 12, zIndex: 2,
};

// Fixed layout: all four waveforms as full-width stacked rows — easier to
// read each one at a glance than when ICG/BP were squeezed into a half-width
// pair underneath PPG/ECG.
const captionSx = {
  fontSize: '0.64rem', color: 'rgba(255,255,255,0.35)', display: 'flex',
  alignItems: 'center', gap: 0.6, px: 0.5,
};

const SignalsPanel: React.FC<SignalsPanelProps> = ({
  isMonitoring, ppgSignal, ecgSignal, icgSignal, bpSignal, bufferFrames,
  showEcg = true, showIcg = true, showBp = true, groundTruth,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {groundTruth && (
        <Box sx={captionSx}>
          <Box sx={{ width: 14, height: 0, borderTop: '1px dashed rgba(255,255,255,0.45)' }} />
          dashed = real lab-device ground truth
        </Box>
      )}
      <Box sx={tileSx}>
        <Typography sx={labelSx}>PPG</Typography>
        <IPPGChart isMonitoring={isMonitoring} ppgSignal={ppgSignal} bufferFrames={bufferFrames}
          groundTruthSignal={groundTruth?.ppg} />
      </Box>
      {showEcg && (
        <Box sx={tileSx}>
          <Typography sx={labelSx}>ECG</Typography>
          <ECGChart isMonitoring={isMonitoring} ecgSignal={ecgSignal} bufferFrames={bufferFrames}
            groundTruthSignal={groundTruth?.ecg} />
        </Box>
      )}
      {showIcg && (
        <Box sx={tileSx}>
          <Typography sx={labelSx}>ICG</Typography>
          <ICGChart isMonitoring={isMonitoring} icgSignal={icgSignal} bufferFrames={bufferFrames}
            groundTruthSignal={groundTruth?.icg} />
        </Box>
      )}
      {showBp && (
        <Box sx={tileSx}>
          <Typography sx={labelSx}>BP</Typography>
          <BPWaveChart isMonitoring={isMonitoring} bpSignal={bpSignal} bufferFrames={bufferFrames}
            groundTruthSignal={groundTruth?.bp} />
        </Box>
      )}
    </Box>
  );
};

export default SignalsPanel;
